/**
 * IssueRowActions — per-row inline action buttons for the issue list.
 *
 * States (driven by the embedded latest_analysis):
 *   Pending (latest_analysis === null) → primary "Analyze" button
 *   In Progress (status queued | running)  → secondary "Cancel" + spinner
 *   Analyzed (status completed)            → kebab menu → "Re-analyze"
 *
 * All actions are optimistic: the row is patched in local state immediately,
 * then settled when the corresponding SSE event arrives (or reverted on error).
 */

import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';

import { analyses } from '@/helpers/ApiHelper';
import type { AnalysisSummary } from '@/types/issue';

// Shared button style across all three action button variants.
const actionButtonSx = {
  minHeight: 28,
  fontSize: 11,
  fontFamily: '"JetBrains Mono", monospace',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  px: 1.5,
} as const;

export interface IssueRowActionsProps {
  issueId: string;
  latestAnalysis: AnalysisSummary | null;
  /** Called when an action triggers a local state change the parent should know about.
   *  "pending" signals "user clicked Analyze, awaiting server confirmation".
   *  null signals "optimistic cancel — hide analysis until SSE settles". */
  onOptimisticUpdate: (issueId: string, patch: Partial<AnalysisSummary> | 'pending' | null) => void;
  /** Called when an action errors so the parent can revert optimistic state. */
  onRevert: (issueId: string) => void;
}

export function IssueRowActions({
  issueId,
  latestAnalysis,
  onOptimisticUpdate,
  onRevert,
}: IssueRowActionsProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [busy, setBusy] = useState(false);

  const status = latestAnalysis?.status;
  const isInProgress = status === 'queued' || status === 'running';
  const isAnalyzed = status === 'completed';

  function handleMenuOpen(e: React.MouseEvent<HTMLElement>): void {
    setAnchorEl(e.currentTarget);
  }
  function handleMenuClose(): void {
    setAnchorEl(null);
  }

  async function handleAnalyze(): Promise<void> {
    if (busy) return;
    setBusy(true);
    // Optimistic: mark as "pending" — awaiting analysis_started SSE confirmation
    onOptimisticUpdate(issueId, 'pending');
    try {
      await analyses.request(issueId);
    } catch {
      onRevert(issueId);
    } finally {
      setBusy(false);
    }
  }

  async function handleCancel(): Promise<void> {
    if (!latestAnalysis?.id || busy) return;
    setBusy(true);
    // Optimistic: immediately hide the analysis (null) while we wait for the
    // SSE analysis_cancelled event to settle it properly.
    onOptimisticUpdate(issueId, null);
    try {
      await analyses.cancel(latestAnalysis.id);
    } catch {
      onRevert(issueId);
    } finally {
      setBusy(false);
    }
  }

  async function handleReanalyze(): Promise<void> {
    handleMenuClose();
    if (busy) return;
    setBusy(true);
    // Optimistic: flip back to pending (server will confirm via analysis_started)
    onOptimisticUpdate(issueId, 'pending');
    try {
      await analyses.request(issueId, 'claude-sonnet-4-6', 'auto_triage_agentic.v1', true);
    } catch {
      onRevert(issueId);
    } finally {
      setBusy(false);
    }
  }

  // ── Pending: show Analyze ──────────────────────────────────────────────────
  if (!latestAnalysis || (!isInProgress && !isAnalyzed)) {
    return (
      <Button
        variant="contained"
        color="primary"
        size="small"
        disabled={busy}
        onClick={() => { void handleAnalyze(); }}
        sx={actionButtonSx}
      >
        {busy ? (
          <CircularProgress size={12} sx={{ color: 'inherit' }} />
        ) : (
          'Analyze'
        )}
      </Button>
    );
  }

  // ── In Progress: show Cancel + spinner ────────────────────────────────────
  if (isInProgress) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <CircularProgress
          size={14}
          sx={{ color: 'primary.main', flexShrink: 0 }}
          aria-label="Analysis in progress"
        />
        <Button
          variant="outlined"
          color="primary"
          size="small"
          disabled={busy}
          onClick={() => { void handleCancel(); }}
          sx={{
            ...actionButtonSx,
            borderColor: 'primary.main',
            color: 'primary.main',
          }}
        >
          Cancel
        </Button>
      </Box>
    );
  }

  // ── Analyzed: kebab menu → Re-analyze ─────────────────────────────────────
  return (
    <>
      <Tooltip title="More actions">
        <IconButton
          size="small"
          aria-label="More row actions"
          aria-haspopup="true"
          aria-expanded={Boolean(anchorEl)}
          onClick={handleMenuOpen}
          sx={{
            minWidth: 44,
            minHeight: 44,
            color: 'text.secondary',
            '&:hover': { color: 'primary.main' },
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          },
        }}
      >
        <MenuItem
          onClick={() => { void handleReanalyze(); }}
          disabled={busy}
          sx={{ minHeight: 40 }}
        >
          <ListItemIcon>
            <RefreshIcon fontSize="small" sx={{ color: 'primary.main' }} />
          </ListItemIcon>
          <ListItemText
            primary="Re-analyze"
            primaryTypographyProps={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 12,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          />
        </MenuItem>
      </Menu>
    </>
  );
}
